<template>
  <div
    :class="[
      $style.wrapper,
      ...(!disableAnimation ? [percentage >= 100 && 'particles', pulsate && $style.pulsate] : []),
    ]"
    @transitionend="pulsate = false"
  >
    <svg ref="circle" width="36" height="36" viewBox="0 0 36 36" :class="$style.svg">
      <path
        :class="[$style.backgroundCircle, dark && $style.darkTheme]"
        d="M18 2.0845
          a 15.9155 15.9155 0 0 1 0 31.831
          a 15.9155 15.9155 0 0 1 0 -31.831"
      />
      <path
        :class="[$style.circle, circleColors, disableAnimation && $style.transitionDisabled]"
        :stroke-dasharray="circleCircumference"
        :stroke-dashoffset="strokeDashoffset"
        d="M18 2.0845
          a 15.9155 15.9155 0 0 1 0 31.831
          a 15.9155 15.9155 0 0 1 0 -31.831"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { defineComponent, watch, ref, useCssModule, computed, onMounted, type Ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     * set goal number
     */
    goal?: number
    /**
     * set progress level
     */
    actionCount?: number
    /**
     * dark theme
     */
    dark?: boolean
    // Use --goal-progress-color CSS var to set color on the parent level
    /**
     * allows for setting custom goal color, use `--goal-progress-color` CSS var to set color on the parent level
     */
    customColor?: boolean
    /**
     * disable pulsate and confetti animations
     */
    disableAnimation?: boolean
  }>(),
  {
    goal: 0,
    actionCount: 0,
    dark: false,
    customColor: false,
    disableAnimation: false,
  },
)

const style = useCssModule()
const pulsate = ref(false)

const percentage = computed(() =>
  props.actionCount > 0 ? Math.round((Math.min(props.actionCount, props.goal) * 100) / props.goal) : 0,
)
const viewBoxWidth = 36
const circleDiameter = 31.831
const circle = ref<Ref | null>(null)

let computedStyle = ref<CSSStyleDeclaration>()

onMounted(() => {
  computedStyle.value = getComputedStyle(circle.value)
})

const circleCircumference = computed(() => {
  const xPadding = computedStyle.value
    ? parseFloat(computedStyle.value.paddingLeft) + parseFloat(computedStyle.value.paddingRight)
    : 0
  const factor = xPadding ? (circle.value?.clientWidth - xPadding) / viewBoxWidth : 1

  // return full circle when goal is fulfilled
  if (percentage.value >= 100) return 100
  return factor * circleDiameter * Math.PI
})

const strokeDashoffset = computed(
  () => circleCircumference.value - (percentage.value / 100) * circleCircumference.value,
)

const circleColors = computed(() => ({
  [style.red]: percentage.value < 25,
  [style.orange]: percentage.value >= 25 && percentage.value < 50,
  [style.green]: percentage.value >= 50,
  [style.custom]: props.customColor,
}))

watch(
  () => props.actionCount,
  (_, updatedBefore) => {
    if (updatedBefore) {
      pulsate.value = true
      setTimeout(() => (pulsate.value = false), 600)
    }
  },
  { immediate: true },
)

defineComponent({
  name: 'UiGoal',
})
</script>

<style lang="scss" module>
.svg {
  background: none !important;
  padding: 0 !important;
  path {
    fill: none !important;
  }
}

.circle {
  fill: none;
  stroke-width: 4 !important;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.3s ease-in-out;
}

.transitionDisabled {
  transition: none;
}

.backgroundCircle {
  fill: none;
  stroke: theme('colors.goal.background');
  stroke-width: 4 !important;
}
.darkTheme {
  stroke: theme('colors.goal.background-dark');
}

.red {
  stroke: var(--red-40);
}
.orange {
  stroke: var(--orange-40);
}
.green {
  stroke: var(--green-40);
}
.custom {
  stroke: var(--goal-progress-color);
}

.pulsate {
  transform: scale3d(1.4, 1.4, 1.4);
}

/* Particles setup */
.wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
  font-size: theme('fontSize.2xl');
  transition: transform 0.5s cubic-bezier(0.785, 0.135, 0.15, 0.86);
}
.wrapper::before,
.wrapper::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}
.wrapper::before {
  border: solid 2.25rem var(--gray-50);
  width: 4.5rem;
  height: 4.5rem;
  transform: translate(-50%, -50%) scale(0);
}
.wrapper::after {
  width: 0.375rem;
  height: 0.375rem;
  box-shadow:
    0.32476rem -3rem 0 -0.1875rem #ff8080,
    -0.32476rem -2.625rem 0 -0.1875rem #ffed80,
    2.54798rem -1.61656rem 0 -0.1875rem #ffed80,
    1.84982rem -1.89057rem 0 -0.1875rem #a4ff80,
    2.85252rem 0.98418rem 0 -0.1875rem #a4ff80,
    2.63145rem 0.2675rem 0 -0.1875rem #80ffc8,
    1.00905rem 2.84381rem 0 -0.1875rem #80ffc8,
    1.43154rem 2.22414rem 0 -0.1875rem #80c8ff,
    -1.59425rem 2.562rem 0 -0.1875rem #80c8ff,
    -0.84635rem 2.50595rem 0 -0.1875rem #a480ff,
    -2.99705rem 0.35095rem 0 -0.1875rem #a480ff,
    -2.48692rem 0.90073rem 0 -0.1875rem #ff80ed,
    -2.14301rem -2.12438rem 0 -0.1875rem #ff80ed,
    -2.25479rem -1.38275rem 0 -0.1875rem #ff8080;
}
</style>

<style>
.particles {
  will-change: font-size;
  animation: heart 1.5s cubic-bezier(0.85, 0.15, 0.25, 0.95);
}
.particles::before,
.particles::after {
  animation: inherit;
  animation-timing-function: ease-out;
}
.particles::before {
  will-change: transform, border-width, border-color;
  animation-name: bubble;
}
.particles::after {
  will-change: opacity, box-shadow;
  animation-name: sparkles;
}

@keyframes heart {
  0%,
  17.5% {
    font-size: 0;
  }
}
@keyframes bubble {
  15% {
    transform: translate(-50%, -50%) scale(1);
    border-color: var(--gray-50);
    border-width: 2.25rem;
  }
  30%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    border-color: var(--gray-50);
    border-width: 0;
  }
}
@keyframes sparkles {
  0%,
  20% {
    opacity: 0;
  }
  25% {
    opacity: 1;
    box-shadow:
      0.32476rem -2.4375rem 0 0rem #ff8080,
      -0.32476rem -2.0625rem 0 0rem #ffed80,
      2.1082rem -1.26585rem 0 0rem #ffed80,
      1.41004rem -1.53985rem 0 0rem #a4ff80,
      2.30412rem 0.85901rem 0 0rem #a4ff80,
      2.08305rem 0.14233rem 0 0rem #80ffc8,
      0.76499rem 2.33702rem 0 0rem #80ffc8,
      1.18748rem 1.71734rem 0 0rem #80c8ff,
      -1.35019rem 2.0552rem 0 0rem #80c8ff,
      -0.60229rem 1.99916rem 0 0rem #a480ff,
      -2.44865rem 0.22578rem 0 0rem #a480ff,
      -1.93852rem 0.77557rem 0 0rem #ff80ed,
      -1.70323rem -1.77366rem 0 0rem #ff80ed,
      -1.81501rem -1.03204rem 0 0rem #ff8080;
  }
}
</style>
